import Cookies from "js-cookie";
import type { Store } from "vuex";
import type { Router } from "vue-router";

const API_BASE_URL = process.env.VUE_APP_API_URL || "/api";

let storeInstance: Store<any> | null = null;
let routerInstance: Router | null = null;

export function initHttpClient(store: Store<any>, router: Router) {
  storeInstance = store;
  routerInstance = router;
}

export interface ApiResponse<T> {
  data?: T;
  errorCode?: string;
}

export interface ApiError {
  errorCode: string;
}

class HttpClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeaders(): Record<string, string> {
    const token = Cookies.get("auth-token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private async handleResponse<T>(response: Response, endpoint: string): Promise<T> {
    const contentType = response.headers.get("content-type");
    const isJson = contentType?.includes("application/json");

    // Handle 401 Unauthorized
    if (response.status === 401) {
      // Try to parse JSON to check if it has an errorCode
      if (isJson) {
        const errorData = await response.json();
        // If has errorCode (e.g. ERROR_CREDENTIALS from login), it's a validation error, not session expired
        if (errorData.errorCode) {
          throw {
            ...errorData,
            errorCode: errorData.errorCode,
          } as ApiError;
        }
      }
      
      // No errorCode means session expired - logout and redirect
      if (storeInstance && routerInstance) {
        await storeInstance.dispatch("auth/logout", routerInstance);
        await storeInstance.dispatch("notificator/apiError", { errorCode: "UNAUTHORIZED" });
      }
      throw { errorCode: "UNAUTHORIZED" } as ApiError;
    }

    if (response.status === 403 && endpoint === "/users/me") {
      if (storeInstance && routerInstance) {
        await storeInstance.dispatch("auth/logout", routerInstance);
      }
      throw { errorCode: "UNAUTHORIZED" } as ApiError;
    }

    if (!response.ok) {
      if (isJson) {
        const errorData = await response.json();
        throw {
          ...errorData,
          errorCode: errorData.errorCode || "INTERNAL_ERROR",
        } as ApiError;
      }
      throw { errorCode: "INTERNAL_ERROR" } as ApiError;
    }

    if (isJson) {
      const json: ApiResponse<T> = await response.json();
      if (json.errorCode) {
        throw { ...json, errorCode: json.errorCode } as ApiError;
      }
      return json.data as T;
    }

    return {} as T;
  }

  async get<T>(endpoint: string): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeaders(),
        },
      });
      return this.handleResponse<T>(response, endpoint);
    } catch (error: any) {
      if (error?.errorCode) throw error;
      throw { errorCode: "CONNECTION_ERROR" } as ApiError;
    }
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeaders(),
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      return this.handleResponse<T>(response, endpoint);
    } catch (error: any) {
      if (error?.errorCode) throw error;
      throw { errorCode: "CONNECTION_ERROR" } as ApiError;
    }
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeaders(),
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      return this.handleResponse<T>(response, endpoint);
    } catch (error: any) {
      if (error?.errorCode) throw error;
      throw { errorCode: "CONNECTION_ERROR" } as ApiError;
    }
  }

  async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "POST",
        headers: {
          ...this.getAuthHeaders(),
        },
        body: formData,
      });
      return this.handleResponse<T>(response, endpoint);
    } catch (error: any) {
      if (error?.errorCode) throw error;
      throw { errorCode: "CONNECTION_ERROR" } as ApiError;
    }
  }

  async delete<T>(endpoint: string): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeaders(),
        },
      });
      return this.handleResponse<T>(response, endpoint);
    } catch (error: any) {
      if (error?.errorCode) throw error;
      throw { errorCode: "CONNECTION_ERROR" } as ApiError;
    }
  }
}

export const http = new HttpClient(API_BASE_URL);
export default http;
